import {Component, inject, OnInit, signal} from '@angular/core';
import {WizardService} from '../../../../shared/components/wizard/wizard.service';
import {ProjectWizardService} from './services/project-wizard.service';
import {Wizard} from '../../../../shared/components/wizard/wizard.model';
import {ActivatedRoute, Router} from '@angular/router';
import {DirectusService, ModuleFunction} from '@frontend/directus';
import {ToastService} from '@frontend/toast';
import {ProjectWizardGeneralInformationStepComponent} from './project-wizard-general-information-step/project-wizard-general-information-step.component';
import {WizardComponent} from '../../../../shared/components/wizard/wizard.component';
import {ProjectWizardCompanyStepComponent} from './project-wizard-company-step/project-wizard-company-step.component';
import {ProjectWizardOfficeStepComponent} from './project-wizard-office-step/project-wizard-office-step.component';
import {CompanyOfficeFunctionSelectionService} from './services/company-office-function-selection.service';
import {OfficeModuleFunction} from './project-wizard.types';
import {
  CompanyResponse,
  CreateUpdateProjectRequest,
  ModuleFunctionReference,
  ModuleFunctionControllerService,
  OfficeModuleFunctionsRequest,
  ProjectDetailResponse,
  ProjectOfficeResponse,
} from '@frontend/shared-api';
import {lastValueFrom} from 'rxjs';

import {ConfirmationDialogService} from '../../../../shared/components/dialog/confirmation/confirmation-dialog.service';

@Component({
  selector: 'app-project-wizard',
  imports: [WizardComponent],
  templateUrl: './project-wizard.component.html',
  styleUrl: './project-wizard.component.scss',
  providers: [
    CompanyOfficeFunctionSelectionService,
    {
      provide: WizardService,
      useExisting: ProjectWizardService,
    },
  ],
})
export class ProjectWizardComponent implements OnInit {
  projectId: number | null = null;
  wizard = signal<Wizard>({
    title: 'project.title',
    backUrl: 'project',
    steps: [
      {
        index: 1,
        header: 'project.step.generalInformation',
        content: ProjectWizardGeneralInformationStepComponent,
        isValid: false,
      },
      {
        index: 2,
        header: 'project.step.company',
        content: ProjectWizardCompanyStepComponent,
        isValid: false,
      },
      {
        index: 3,
        header: 'project.step.office',
        content: ProjectWizardOfficeStepComponent,
        isValid: false,
      },
    ],
    onComplete: () => {
      void this.onWizardComplete();
    },
  });
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly projectWizardService = inject(ProjectWizardService);
  private readonly route = inject(ActivatedRoute);
  private readonly companySelectionService = inject(CompanyOfficeFunctionSelectionService);
  private readonly directus = inject(DirectusService);
  private readonly companySelection = inject(CompanyOfficeFunctionSelectionService);
  private readonly moduleFunctionControllerService = inject(ModuleFunctionControllerService);
  private readonly confirmDialogService = inject(ConfirmationDialogService);

  ngOnInit(): void {
    const {project, companies} = this.route.snapshot.data as {
      project: ProjectDetailResponse;
      companies: CompanyResponse[];
    };
    if (!project) {
      this.projectWizardService.isEdit.set(false);
      return;
    }

    this.projectId = project.id;
    this.projectWizardService.isEdit.set(true);

    this.companySelectionService.setCompaniesData(companies);

    this.prefillStepOne(project);
    this.prefillStepTwo(project);
    this.prefillStepThree(project);
  }

  private async onWizardComplete(): Promise<void> {
    const requestPayload = this.projectWizardService.buildFinalRequest();
    const hasConfirmationToProceed =
      await this.checkIfContractFunctionsMissingAndConfirm(requestPayload);

    if (!hasConfirmationToProceed) {
      return;
    }

    if (this.projectWizardService.isEdit() && this.projectId != null) {
      await lastValueFrom(this.projectWizardService.updateProject(this.projectId, requestPayload));
      await this.toast.showSuccess(
        'wizard.project.toast.update.summary',
        'wizard.project.toast.update.detail',
      );
    } else {
      await lastValueFrom(this.projectWizardService.createProject(requestPayload));
      await this.toast.showSuccess(
        'wizard.project.toast.create.summary',
        'wizard.project.toast.create.detail',
      );
    }

    await this.router.navigate([this.wizard().backUrl]);
  }

  private async checkIfContractFunctionsMissingAndConfirm(
    requestPayload: CreateUpdateProjectRequest,
  ): Promise<boolean> {
    const contractFunctions = await lastValueFrom(
      this.moduleFunctionControllerService.getAllModuleFunctions(),
    );
    const availableContractFunctionIds = new Set(contractFunctions.map((fn) => fn.directusId));
    const selectedFunctionIds = new Set(
      Array.from<OfficeModuleFunctionsRequest>(requestPayload.offices).flatMap((office) =>
        Array.from(office.moduleFunctions),
      ),
    );

    const hasAllContractFunctionsSelected = Array.from(availableContractFunctionIds).every((id) =>
      selectedFunctionIds.has(id),
    );
    if (hasAllContractFunctionsSelected) {
      return true;
    }

    return new Promise<boolean>((resolve) => {
      this.confirmDialogService.confirm({
        headerKey: 'project.functions.incompleteSelectionHeader',
        messageKey: 'project.functions.incompleteSelectionMessage',
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  private prefillStepOne(project: ProjectDetailResponse): void {
    this.projectWizardService.updateStepData({
      stepIndex: 1,
      data: {
        name: project.name,
        description: project.description,
        yearsInScope: project.yearsInScope,
      },
    });
  }

  private prefillStepTwo(project: ProjectDetailResponse): void {
    const officeResponses = Array.from<ProjectOfficeResponse>(project.offices);

    const companyIds = new Set<number>(
      officeResponses.flatMap((office) => {
        const officeViewModel = this.companySelectionService.getOfficeViewModel(office.officeId);
        return officeViewModel ? [officeViewModel.companyId] : [];
      }),
    );

    this.projectWizardService.updateStepData({
      stepIndex: 2,
      data: companyIds,
    });

    companyIds.forEach((companyId) => this.companySelectionService.toggleCompany(companyId));
  }

  private async prefillStepThree(project: ProjectDetailResponse): Promise<void> {
    const officeResponses = Array.from<ProjectOfficeResponse>(project.offices);
    const officeModuleFunctions: OfficeModuleFunction[] = officeResponses.map((office) => ({
      officeId: office.officeId,
      moduleFunctions: new Set(
        Array.from<ModuleFunctionReference>(office.functions).map((fn) => fn.directusId),
      ),
    }));

    const modulesMap = await lastValueFrom(this.directus.loadModuleFunctionsGroupedByModule());

    const allFuncs = Array.from(modulesMap.values()).flatMap((m) => m.functions ?? []);
    const funcById = new Map<number, ModuleFunction>(allFuncs.map((f) => [f.id, f]));

    officeModuleFunctions.forEach(({officeId, moduleFunctions}) => {
      const fullFuncs: ModuleFunction[] = Array.from(moduleFunctions)
        .map((directusId) => funcById.get(Number(directusId)))
        .filter((f): f is ModuleFunction => !!f);

      this.companySelection.toggleOffice(officeId);

      this.companySelection.setOfficeFunctions(officeId, fullFuncs);
    });

    this.projectWizardService.updateStepData({
      stepIndex: 3,
      data: officeModuleFunctions,
    });
  }
}
